/**
 * R232q5aN: Client-side Zustand store for mount progression.
 *
 *  - `ridingSkill`     — server-replicated value (0/75/150/225/300).
 *  - `coldWeatherFlying` — server-replicated bool.
 *  - `ownedMountIds`   — derived from inventory + bank (any item id starting
 *                        with `mount-` is treated as owned).
 *  - `activeMountId`   — last mount the player picked in MountSelector;
 *                        persisted to localStorage so refresh keeps it.
 *
 * Server messages:
 *   mount-toggle      → realm replies with mount-toggle-result
 *   learn-riding      → reply learn-riding-result
 *   learn-cwf         → reply learn-cwf-result
 *
 * The store also exposes `summon` / `dismount` helpers that the MountSelector
 * and global `/mount` slash-command call. They build the full payload from
 * data/mounts.ts so the server runs the WotLK validator.
 */

import { create } from 'zustand';
import { MOUNT_BY_ID, type MountDef } from '../data/mounts.js';
import { getRoom } from '../game/realmClient.js';

const ACTIVE_KEY = 'aiwari-active-mount';

interface ToastFn {
  (msg: string, kind?: 'ok' | 'err'): void;
}

interface MountStore {
  ridingSkill: number;          // 0 / 75 / 150 / 225 / 300
  coldWeatherFlying: boolean;
  activeMountId: string;
  /** Last server toast — UI subscribes to render it. */
  lastToast: { msg: string; kind: 'ok' | 'err'; ts: number } | null;
  setRidingSkill: (skill: number) => void;
  setColdWeatherFlying: (v: boolean) => void;
  setActiveMount: (id: string) => void;
  pushToast: (msg: string, kind?: 'ok' | 'err') => void;

  /** Send mount-toggle to summon `mountId` (or current active). */
  summon: (mountId?: string) => void;
  /** Toggle off — sends mount-toggle without payload, server unmounts. */
  dismount: () => void;
  /** Send learn-riding for the next tier. */
  buyTier: (tierId: 'apprentice' | 'journeyman' | 'expert' | 'artisan') => void;
  /** Send learn-cwf. */
  buyCWF: () => void;
}

function loadActive(): string {
  try { return localStorage.getItem(ACTIVE_KEY) ?? ''; } catch { return ''; }
}
function saveActive(id: string): void {
  try { localStorage.setItem(ACTIVE_KEY, id); } catch { /* noop */ }
}

export const useMounts = create<MountStore>((set) => ({
  ridingSkill: 0,
  coldWeatherFlying: false,
  activeMountId: loadActive(),
  lastToast: null,
  setRidingSkill: (skill) => set({ ridingSkill: skill }),
  setColdWeatherFlying: (v) => set({ coldWeatherFlying: v }),
  setActiveMount: (id) => {
    saveActive(id);
    set({ activeMountId: id });
  },
  pushToast: (msg, kind = 'ok') => set({ lastToast: { msg, kind, ts: Date.now() } }),
  summon: (mountId) => {
    const id = mountId ?? loadActive();
    if (!id) return;
    const def: MountDef | undefined = MOUNT_BY_ID.get(id);
    const room = getRoom() as unknown as { send: (k: string, m: unknown) => void } | null;
    if (!room) return;
    if (!def) {
      // Legacy fallback — caller wants to use bag-only mount.
      room.send('mount-toggle', { mountId: id });
      return;
    }
    room.send('mount-toggle', {
      mountId: def.id,
      faction: def.faction,
      type: def.type,
      classOnly: def.classOnly,
      requiredRidingSkill: def.requiredRidingSkill,
      requiresCWF: def.requiresCWF,
      minLevel: def.minLevel,
      speedMul: def.speedMul,
    });
  },
  dismount: () => {
    const room = getRoom() as unknown as { send: (k: string, m: unknown) => void } | null;
    if (room) room.send('mount-toggle', {});
  },
  buyTier: (tierId) => {
    const room = getRoom() as unknown as { send: (k: string, m: unknown) => void } | null;
    if (room) room.send('learn-riding', { tierId });
  },
  buyCWF: () => {
    const room = getRoom() as unknown as { send: (k: string, m: unknown) => void } | null;
    if (room) room.send('learn-cwf', {});
  },
}));

/**
 * Helper toast plumbing for callers — listens to server replies once.
 * Must be invoked after the realm room is connected (typically inside
 * `realmClient.ts` after `await client.joinOrCreate()`).
 *
 * Designed idempotent: subsequent calls re-attach without leaking.
 */
let _wired = false;
export function wireMountServerMessages(room: {
  onMessage: (k: string, fn: (data: unknown) => void) => void;
}, toastFn?: ToastFn): void {
  if (_wired) return;
  _wired = true;
  const push = (msg: string, kind: 'ok' | 'err' = 'ok') => {
    if (toastFn) toastFn(msg, kind);
    useMounts.getState().pushToast(msg, kind);
  };
  room.onMessage('mount-toggle-result', (data: unknown) => {
    const r = data as { mounted?: boolean; mountId?: string; error?: string; fallbackToGround?: boolean; speedMul?: number };
    if (r?.error) {
      push(r.error, 'err');
      return;
    }
    if (r?.mounted) {
      const def = MOUNT_BY_ID.get(r.mountId ?? '');
      let msg = `Скакун призван: ${def?.name.ru ?? r.mountId}`;
      if (r.fallbackToGround) msg += ' (только наземный режим в этой зоне).';
      push(msg, 'ok');
    } else {
      push('Скакун снят.', 'ok');
    }
  });
  room.onMessage('learn-riding-result', (data: unknown) => {
    const r = data as { ok?: boolean; error?: string; tierRu?: string; scLeft?: number; newSkill?: number };
    if (!r?.ok) {
      push(r?.error ?? 'Не удалось.', 'err');
      return;
    }
    if (typeof r.newSkill === 'number') useMounts.getState().setRidingSkill(r.newSkill);
    push(`Изучено: ${r.tierRu ?? 'ранг'}. Осталось ${r.scLeft ?? 0} 💎.`, 'ok');
  });
  room.onMessage('learn-cwf-result', (data: unknown) => {
    const r = data as { ok?: boolean; error?: string; scLeft?: number };
    if (!r?.ok) {
      push(r?.error ?? 'Не удалось.', 'err');
      return;
    }
    useMounts.getState().setColdWeatherFlying(true);
    push(`Изучено: «Ледяной полёт». Осталось ${r.scLeft ?? 0} 💎.`, 'ok');
  });
}
